import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { url } from "src/api";

/*
 * "See opsanio in action" - the product showcase.
 *
 * One dedicated card per dashboard (a real dark-theme screenshot from the live
 * appliance + an honest, fact-based caption), laid out as a responsive grid so
 * every dashboard has its own always-visible entry. This replaced the former
 * 9-slider / ~43-image appliance sprawl (a Hick's / Miller's-Law problem) and a
 * tabbed version, surfacing the breadth of "one appliance, full visibility".
 *
 * Cards are non-interactive (marketing gallery), so they have no hover effect.
 * Screenshots load from the backend image mount (`${url}v2/images/...`).
 * All copy is i18n with English fallbacks.
 */

const tPrefix = "anketa.autonubil.home.showcase";

interface Card {
  key: string;
  image: string;
  titleFallback: string;
  descFallback: string;
  altFallback: string;
}

const cards: Card[] = [
  {
    key: "overview",
    image: "opsanio-dash-overview.png",
    titleFallback: "Unified overview",
    descFallback:
      "Opsane Score, assets, exposure, events and flows - one live command centre.",
    altFallback: "opsanio overview dashboard with live KPIs across every domain",
  },
  {
    key: "assets",
    image: "opsanio-dash-assets.png",
    titleFallback: "Asset inventory",
    descFallback:
      "Every device, VM and service, merged across 40+ sources and mapped to BSI / ISO controls.",
    altFallback: "opsanio asset inventory with BSI control tags and multi-source provenance",
  },
  {
    key: "cves",
    image: "opsanio-dash-cves.png",
    titleFallback: "Vulnerability prioritisation",
    descFallback:
      "CVEs ranked by real-world exploitability (EPSS / KEV), correlated to your actual assets.",
    altFallback: "opsanio vulnerability dashboard ranking CVEs by exposure",
  },
  {
    key: "services",
    image: "opsanio-dash-services.png",
    titleFallback: "Service & port inventory",
    descFallback:
      "Every listening service and port across the estate, with TLS coverage at a glance.",
    altFallback: "opsanio services dashboard listing ports and devices",
  },
  {
    key: "certificates",
    image: "opsanio-dash-certificates.png",
    titleFallback: "Certificate lifecycle",
    descFallback:
      "Expiry tracking for every certificate - expired, critical, warning and safe.",
    altFallback: "opsanio certificates dashboard with expiry-status charts",
  },
  {
    key: "flows",
    image: "opsanio-dash-flows.png",
    titleFallback: "Network flow analysis",
    descFallback:
      "Client → service → server paths from IPFIX / NetFlow / sFlow, traced in one Sankey view.",
    altFallback: "opsanio network flow Sankey from clients through services to servers",
  },
  {
    key: "investigate",
    image: "opsanio-dash-investigate.png",
    titleFallback: "Source coverage",
    descFallback:
      "See which integrations see which devices - coverage gaps across every source at a glance.",
    altFallback: "opsanio investigate view with an UpSet plot of device coverage per source",
  },
  {
    key: "workflows",
    image: "opsanio-dash-workflows.png",
    titleFallback: "Sigma detection rules",
    descFallback:
      "3,000+ community Sigma rules in a managed pool - adopt, edit and test each one against a sample event.",
    altFallback: "opsanio rule editor showing a Sigma rule with ATT&CK tags and a test event",
  },
  {
    key: "compliance",
    image: "opsanio-dash-compliance.png",
    titleFallback: "Compliance posture",
    descFallback:
      "The Opsane Score across 49 OCF controls - viewed through NIS2, ISO 27001, BSI IT-Grundschutz, CIS Controls and more.",
    altFallback: "opsanio compliance dashboard with the Opsane Score and framework lenses",
  },
];

const DashboardCard: React.FC<{ card: Card }> = ({ card }) => {
  const { translate } = useAnketaI18n();
  return (
    <div
      style={{
        background: "var(--ak-surface)",
        border: "1px solid var(--ak-border)",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 1px 4px var(--ak-shadow)",
      }}
    >
      <div style={{ aspectRatio: "16 / 10", overflow: "hidden", background: "var(--ak-navy-3)" }}>
        <img
          src={`${url}v2/images/${card.image}`}
          alt={translate(`${tPrefix}.${card.key}.alt`, { defaultMessage: card.altFallback } as any)}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
          }}
        />
      </div>
      <div style={{ padding: "20px 22px 24px" }}>
        <h3
          style={{
            margin: 0,
            fontSize: "1.15rem",
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "var(--ak-text)",
          }}
        >
          {translate(`${tPrefix}.${card.key}.title`, { defaultMessage: card.titleFallback } as any)}
        </h3>
        <p
          style={{
            margin: "8px 0 0",
            fontSize: "0.95rem",
            lineHeight: 1.55,
            color: "var(--ak-text-secondary)",
          }}
        >
          {translate(`${tPrefix}.${card.key}.desc`, { defaultMessage: card.descFallback } as any)}
        </p>
      </div>
    </div>
  );
};

export const AutonubilProductShowcase: React.FC = () => {
  const { translate } = useAnketaI18n();

  return (
    <section
      id="product"
      style={{
        background: "var(--ak-page-bg)",
        padding: "clamp(64px, 9vw, 104px) clamp(24px, 5vw, 48px)",
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        {/* header */}
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--ak-accent)",
            }}
          >
            {translate(`${tPrefix}.eyebrow`, { defaultMessage: "The product" } as any)}
          </p>
          <h2
            style={{
              margin: "12px 0 0",
              fontSize: "clamp(1.9rem, 3.4vw, 2.8rem)",
              fontWeight: 800,
              letterSpacing: "-0.025em",
              lineHeight: 1.1,
              color: "var(--ak-text)",
            }}
          >
            {translate(`${tPrefix}.title`, { defaultMessage: "See opsanio in action" } as any)}
          </h2>
          <p
            style={{
              margin: "16px auto 0",
              fontSize: "1.05rem",
              lineHeight: 1.65,
              color: "var(--ak-text-secondary)",
            }}
          >
            {translate(`${tPrefix}.subtitle`, {
              defaultMessage:
                "One appliance, full visibility - every dashboard from the live product, not mock-ups. Identifying details blurred.",
            } as any)}
          </p>
        </div>

        {/* dashboard cards */}
        <div
          style={{
            marginTop: 44,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 24,
          }}
        >
          {cards.map((c) => (
            <DashboardCard key={c.key} card={c} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AutonubilProductShowcase;
